/**
 * DialpadManagerPage · v2.8+E338 · Dialpad Manager
 * ══════════════════════════════════════════════════════════════════════════
 * Single admin home for ALL Dialpad configuration:
 *   • Webhook Target URL + signing secret (paste into Dialpad)
 *   • Dialpad API credentials (stored server-side, masked)
 *   • Connection status
 *   • Call Activity Log + one-tap missed-call callback
 *   • Per-user Dialpad NUMBER assignment table (dispatchers/staff/partners)
 *
 * Gated to Super Users + Admins (backend enforces via _is_full; a 403 here
 * renders an access-denied card). All changes are DB-backed and take effect
 * immediately — no redeployment.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import adminApi from "@/lib/adminApi";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Copy, Eye, EyeOff, RotateCw, Phone, PhoneCall, PhoneMissed, Voicemail,
  CheckCircle2, AlertTriangle, Loader2, RefreshCw, History, ShieldAlert, ShieldCheck, Users, Search, ArrowLeft, Zap,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function copy(text, msg) {
  try { navigator.clipboard.writeText(text || ""); toast.success(msg || "Copied"); }
  catch { toast.error("Copy failed"); }
}
function fmtPhone(raw) {
  const d = String(raw || "").replace(/\D/g, "").slice(-10);
  if (d.length !== 10) return raw || "—";
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}
function fmtAgo(iso) {
  if (!iso) return "";
  try {
    const secs = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (secs < 60) return "just now";
    const m = Math.floor(secs / 60); if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  } catch { return ""; }
}
function fmtDuration(sinceIso, nowTs) {
  if (!sinceIso) return "";
  try {
    let s = Math.max(0, Math.floor((nowTs - new Date(sinceIso).getTime()) / 1000));
    const h = Math.floor(s / 3600); s -= h * 3600;
    const m = Math.floor(s / 60); const sec = s - m * 60;
    const mm = String(m).padStart(2, "0"), ss = String(sec).padStart(2, "0");
    return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
  } catch { return ""; }
}
const ROLE_LABEL = {
  dispatcher: "Dispatcher", team_leader: "Team Leader", field_supervisor: "Field Supervisor",
  supervisor: "Supervisor", operating_partner: "Operating Partner", general_partner: "General Partner",
  developer: "Developer", admin: "Admin", platform_owner: "Platform Owner", staff: "Staff",
};

export default function DialpadManagerPage() {
  const navigate = useNavigate();
  const [dp, setDp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tokenDraft, setTokenDraft] = useState("");
  const [userIdDraft, setUserIdDraft] = useState("");
  const [revealSecret, setRevealSecret] = useState(false);
  const [regenBusy, setRegenBusy] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const [regBusy, setRegBusy] = useState(false);
  const [calls, setCalls] = useState([]);
  const [callsLoading, setCallsLoading] = useState(false);
  const [startBusy, setStartBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/dialpad/settings");
      setDp(data);
      setUserIdDraft(data?.dialpad_user_id || "");
      setDenied(false);
    } catch (e) {
      if (e?.response?.status === 403) setDenied(true);
      else toast.error(e?.response?.data?.detail || "Failed to load Dialpad settings");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCalls = useCallback(async () => {
    setCallsLoading(true);
    try {
      const { data } = await adminApi.get("/admin/dialpad/calls?limit=25");
      setCalls(data?.calls || []);
    } catch { /* silent */ } finally { setCallsLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (denied) return undefined;
    loadCalls();
    const t = setInterval(() => { if (!document.hidden) loadCalls(); }, 15000);
    return () => clearInterval(t);
  }, [loadCalls, denied]);

  const save = async (patch, okMsg) => {
    setSaving(true);
    try {
      const { data } = await adminApi.put("/admin/dialpad/settings", patch);
      setDp(data);
      setUserIdDraft(data?.dialpad_user_id || "");
      if (okMsg) toast.success(okMsg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };
  const saveToken = async () => {
    const tok = (tokenDraft || "").trim();
    if (!tok) { toast.error("Paste your Dialpad API token first"); return; }
    await save({ api_token: tok }, "Dialpad API token saved");
    setTokenDraft("");
  };
  const registerWebhook = async () => {
    setRegBusy(true);
    try {
      const { data } = await adminApi.post("/admin/dialpad/register-webhook");
      if (data?.ok) {
        toast.success("Connected — Dialpad will now push call events", { description: "Place a test call to confirm the caller card pops." });
        await load();
      } else {
        toast.error(data?.detail || "Could not connect to Dialpad");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not connect to Dialpad");
    } finally { setRegBusy(false); }
  };
  const unregisterWebhook = async () => {
    setRegBusy(true);
    try {
      await adminApi.post("/admin/dialpad/unregister-webhook");
      toast.success("Disconnected from Dialpad");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to disconnect");
    } finally { setRegBusy(false); }
  };
  const regenerateSecret = async () => {
    setRegenBusy(true);
    try {
      const { data } = await adminApi.put("/admin/dialpad/settings", { regenerate_secret: true });
      setDp(data);
      toast.success("New webhook secret generated — update it in Dialpad");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to regenerate");
    } finally { setRegenBusy(false); }
  };
  const simulate = async (state) => {
    const ph = (testPhone || "").trim();
    if (!ph) { toast.error("Enter a phone number to simulate"); return; }
    setTestBusy(true);
    try {
      const { data } = await adminApi.post("/admin/dialpad/test-call", { phone: ph, state });
      const m = data?.match || {};
      if (state === "missed") toast.success(`Simulated missed call · ${ph}`, { description: "Logged to Call Activity." });
      else toast.success(m.matched ? `Simulated call · matched ${m.customer?.name || ph}` : `Simulated call · new caller ${ph}`, { description: "Your open Dispatcher form populates within ~5s." });
      setTimeout(loadCalls, 600);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Simulation failed");
    } finally { setTestBusy(false); }
  };
  const startBookingFromCall = async (call) => {
    if (!call?.phone) return;
    setStartBusy(call.call_id);
    try {
      await adminApi.get("/admin/caller-match", { params: { phone: call.phone, set_active: 1 } });
      await adminApi.post(`/admin/dialpad/calls/${encodeURIComponent(call.call_id)}/mark-handled`).catch(() => {});
      try { window.sessionStorage.setItem("rtg_dialpad_force", "1"); } catch { /* ignore */ }
      window.history.replaceState({}, "", "/admin?tab=driver-mgmt#sub=book-ride");
      window.dispatchEvent(new CustomEvent("rtg:admin-select-tab", { detail: { tab: "driver-mgmt", sub: "book-ride" } }));
      window.location.assign("/admin?tab=driver-mgmt#sub=book-ride");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start a booking for this caller");
    } finally { setStartBusy(null); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center" data-testid="dialpad-manager-loading">
        <div className="flex items-center gap-2 text-zinc-400"><Loader2 className="h-5 w-5 animate-spin"/>Loading Dialpad Manager…</div>
      </div>
    );
  }
  if (denied) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center p-6" data-testid="dialpad-manager-denied">
        <Card className="bg-zinc-900 border-zinc-800 p-8 max-w-md text-center">
          <ShieldAlert className="h-10 w-10 text-amber-400 mx-auto mb-3"/>
          <h2 className="text-lg font-bold mb-1">Restricted</h2>
          <p className="text-sm text-zinc-400">Only Super Users and Admins can access Dialpad Manager.</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/admin")}
            data-testid="dialpad-back-to-console-denied"
            className="mt-4 h-8 px-3 text-xs border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5"/>Back to Console
          </Button>
        </Card>
      </div>
    );
  }

  const webhookUrl = `${BACKEND}/api/webhooks/dialpad`;
  const status = dp?.connection_status || "disconnected";
  const statusPill = {
    connected: { label: "Connected & Active", cls: "bg-emerald-500/20 text-emerald-300 border-emerald-400/40", Icon: CheckCircle2 },
    webhook_only: { label: "Webhook Ready · No API Token", cls: "bg-amber-500/20 text-amber-300 border-amber-400/40", Icon: AlertTriangle },
    disconnected: { label: "Not Connected", cls: "bg-zinc-700/40 text-zinc-400 border-zinc-600", Icon: AlertTriangle },
  }[status];

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 px-4 sm:px-6 lg:px-8 py-6" data-testid="dialpad-manager-page">
      <div className="max-w-5xl mx-auto space-y-5">
        {/* Back to Console */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/admin")}
          data-testid="dialpad-back-to-console"
          className="h-8 px-2 -ml-2 text-xs text-zinc-400 hover:text-amber-300 hover:bg-zinc-900"
        >
          <ArrowLeft className="h-4 w-4 mr-1.5"/>Back to Console
        </Button>
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-lg bg-yellow-400 text-zinc-950 flex items-center justify-center shadow"><PhoneCall className="h-5 w-5"/></div>
              <h1 className="text-2xl font-bold">Dialpad Manager</h1>
            </div>
            <p className="text-sm text-zinc-500 mt-1 max-w-2xl">Configure Dialpad, manage API credentials, and assign each dispatcher their own Dialpad number so calls pop only on their screen.</p>
          </div>
          <Badge className={`${statusPill.cls} text-xs`} data-testid="dialpad-connection-status">
            <statusPill.Icon className="h-3.5 w-3.5 mr-1"/>{statusPill.label}
          </Badge>
        </div>

        {/* Configuration */}
        <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="dialpad-config-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-lg">Connection &amp; Webhook</h2>
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-zinc-500">Integration</span>
              <Switch data-testid="dialpad-enabled-toggle" checked={!!dp.enabled} disabled={saving}
                onCheckedChange={(v) => save({ enabled: v }, v ? "Dialpad enabled" : "Dialpad disabled")}/>
            </div>
          </div>

          {/* Webhook URL */}
          <div className="mb-4">
            <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Webhook Target URL</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input readOnly value={webhookUrl} data-testid="dialpad-webhook-url" className="bg-zinc-950 border-zinc-700 font-mono text-xs"/>
              <Button variant="outline" size="sm" data-testid="dialpad-copy-url-btn" onClick={() => copy(webhookUrl, "Webhook URL copied")} className="bg-zinc-950 border-zinc-700 flex-shrink-0"><Copy className="h-3.5 w-3.5"/></Button>
            </div>
            <p className="text-[10px] text-zinc-500 mt-1">Paste into Dialpad&apos;s webhook settings for <code className="text-amber-300">call.ringing</code> and <code className="text-amber-300">call.connected</code> events.</p>
          </div>

          {/* Signing secret */}
          <div className="mb-4">
            <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Webhook Signing Secret</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input readOnly type={revealSecret ? "text" : "password"} value={dp.webhook_secret || ""} data-testid="dialpad-webhook-secret" className="bg-zinc-950 border-zinc-700 font-mono text-xs"/>
              <Button variant="outline" size="sm" data-testid="dialpad-reveal-secret-btn" onClick={() => setRevealSecret((s) => !s)} className="bg-zinc-950 border-zinc-700 flex-shrink-0">{revealSecret ? <EyeOff className="h-3.5 w-3.5"/> : <Eye className="h-3.5 w-3.5"/>}</Button>
              <Button variant="outline" size="sm" data-testid="dialpad-copy-secret-btn" onClick={() => copy(dp.webhook_secret, "Signing secret copied")} className="bg-zinc-950 border-zinc-700 flex-shrink-0"><Copy className="h-3.5 w-3.5"/></Button>
              <Button variant="outline" size="sm" data-testid="dialpad-regen-secret-btn" onClick={regenerateSecret} disabled={regenBusy || dp.webhook_secret_from_env} className="bg-zinc-950 border-zinc-700 flex-shrink-0" title={dp.webhook_secret_from_env ? "Managed by an environment secret" : "Generate a new secret"}>{regenBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <RotateCw className="h-3.5 w-3.5"/>}</Button>
            </div>
            <p className="text-[10px] text-zinc-500 mt-1">{dp.webhook_secret_from_env ? "Set from an environment secret (DIALPAD_WEBHOOK_SECRET) — regeneration disabled." : "Dialpad signs each event as an HS256 JWT with this secret; every webhook is verified before it is trusted."}</p>
          </div>

          {/* API token */}
          <div className="mb-4">
            <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Dialpad API Credentials</Label>
            {dp.api_token_configured && (
              <div className="text-[11px] text-emerald-300 mt-1 flex items-center gap-1" data-testid="dialpad-token-status"><CheckCircle2 className="h-3 w-3"/>Configured{dp.api_token_hint ? ` · ${dp.api_token_hint}` : ""}{dp.api_token_from_env ? " · from env" : ""}</div>
            )}
            <div className="flex items-center gap-2 mt-1">
              <Input type="password" value={tokenDraft} onChange={(e) => setTokenDraft(e.target.value)} placeholder={dp.api_token_configured ? "Paste a new token to replace…" : "Paste your Dialpad API key / OAuth token"} data-testid="dialpad-token-input" disabled={dp.api_token_from_env} className="bg-zinc-950 border-zinc-700 font-mono text-xs"/>
              <Button data-testid="dialpad-save-token-btn" onClick={saveToken} disabled={saving || dp.api_token_from_env} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold flex-shrink-0">Save</Button>
            </div>
            <p className="text-[10px] text-zinc-500 mt-1">Stored securely server-side; never shown in full. Needed for auto Screen-Pop into a dispatcher&apos;s browser.</p>
          </div>

          {/* Connect Dialpad — auto-register webhook + call subscription */}
          <div className={`mb-4 rounded-lg border p-3 ${dp.webhook_registered ? "border-emerald-500/40 bg-emerald-500/5" : "border-amber-500/40 bg-amber-500/5"}`} data-testid="dialpad-connect-block">
            {dp.webhook_registered ? (
              <div>
                <div className="text-sm font-semibold text-emerald-300 flex items-center gap-1.5" data-testid="dialpad-webhook-connected">
                  <CheckCircle2 className="h-4 w-4"/>Dialpad is connected — receiving call events
                </div>
                <p className="text-[11px] text-zinc-400 mt-1">
                  Subscription <span className="font-mono text-zinc-300">{dp.dialpad_subscription_id || dp.dialpad_webhook_id}</span> pushes ringing / connected / hangup events to this app.
                  {dp.webhook_registered_url ? <> Target: <span className="font-mono break-all">{dp.webhook_registered_url}</span>.</> : null}
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <Button variant="outline" size="sm" data-testid="dialpad-reconnect-btn" onClick={registerWebhook} disabled={regBusy} className="bg-zinc-950 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 h-8 text-xs">
                    {regBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1"/> : <RefreshCw className="h-3.5 w-3.5 mr-1"/>}Re-register
                  </Button>
                  <Button variant="ghost" size="sm" data-testid="dialpad-disconnect-btn" onClick={unregisterWebhook} disabled={regBusy} className="text-zinc-500 hover:text-rose-300 h-8 text-xs">Disconnect</Button>
                </div>
              </div>
            ) : (
              <div>
                <div className="text-sm font-semibold flex items-center gap-1.5"><Zap className="h-4 w-4 text-amber-300"/>Connect Dialpad to start receiving calls</div>
                <p className="text-[11px] text-zinc-400 mt-1">
                  Saving the API token alone does <b>not</b> make Dialpad send call events — Dialpad needs a webhook + call-event subscription pointing at this app. Tap Connect and we&apos;ll register it for you (using the API token above). Without this, no caller card will pop.
                </p>
                <Button data-testid="dialpad-connect-btn" onClick={registerWebhook} disabled={regBusy || !dp.api_token_configured} className="mt-2 h-9 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold text-xs">
                  {regBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5"/> : <Zap className="h-4 w-4 mr-1.5"/>}Connect Dialpad
                </Button>
                {!dp.api_token_configured && <p className="text-[10px] text-amber-300 mt-1">Add & save your Dialpad API token first.</p>}
              </div>
            )}
          </div>

          {/* Auto screen-pop */}
          <div className="flex items-start justify-between gap-3 rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
            <div className="min-w-0">
              <div className="text-sm font-semibold">Auto Screen-Pop</div>
              <p className="text-[11px] text-zinc-500 mt-0.5">When a call rings, ask Dialpad to open the caller-match page in the dispatcher&apos;s browser. A default Dialpad user id is used when a caller&apos;s line has no per-user id set.</p>
              <Input value={userIdDraft} onChange={(e) => setUserIdDraft(e.target.value)} onBlur={() => { if (userIdDraft !== (dp.dialpad_user_id || "")) save({ dialpad_user_id: userIdDraft }); }} placeholder="Default Dialpad user id (optional)" data-testid="dialpad-userid-input" className="bg-zinc-950 border-zinc-700 text-xs max-w-xs mt-2"/>
            </div>
            <Switch data-testid="dialpad-screenpop-toggle" checked={!!dp.auto_screenpop} disabled={saving} onCheckedChange={(v) => save({ auto_screenpop: v }, v ? "Auto screen-pop on" : "Auto screen-pop off")}/>
          </div>

          {/* Simulate */}
          <div className="mt-4 rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-3">
            <div className="text-[10px] uppercase tracking-widest text-yellow-300 font-bold mb-1 flex items-center gap-1"><Phone className="h-3 w-3"/>Simulate a call (test)</div>
            <p className="text-[11px] text-zinc-500 mb-2">Fires an inbound-call event with no Dialpad needed — routed to YOUR own session.</p>
            <div className="flex items-center gap-2 flex-wrap">
              <Input value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="e.g. (405) 555-0188" data-testid="dialpad-test-phone-input" className="bg-zinc-950 border-zinc-700 text-xs max-w-xs"/>
              <Button variant="outline" data-testid="dialpad-test-call-btn" onClick={() => simulate("ringing")} disabled={testBusy} className="bg-zinc-950 border-yellow-500/50 text-yellow-300 hover:bg-yellow-500/10 flex-shrink-0">{testBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1"/> : <PhoneCall className="h-3.5 w-3.5 mr-1"/>}Simulate</Button>
              <Button variant="outline" data-testid="dialpad-test-missed-btn" onClick={() => simulate("missed")} disabled={testBusy} className="bg-zinc-950 border-rose-500/50 text-rose-300 hover:bg-rose-500/10 flex-shrink-0">{testBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1"/> : <PhoneMissed className="h-3.5 w-3.5 mr-1"/>}Simulate Missed</Button>
            </div>
          </div>
        </Card>

        {/* Per-user assignment */}
        <UserAssignmentTable />

        {/* Call Activity */}
        <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="dialpad-activity">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-lg flex items-center gap-2"><History className="h-4 w-4"/>Call Activity</h2>
            <Button variant="ghost" size="sm" data-testid="dialpad-activity-refresh" onClick={loadCalls} className="h-7 px-2 text-[11px] text-zinc-400 hover:text-zinc-200">{callsLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <RefreshCw className="h-3.5 w-3.5"/>}</Button>
          </div>
          {calls.length === 0 ? (
            <p className="text-[12px] text-zinc-600 italic py-4 text-center" data-testid="dialpad-activity-empty">No inbound calls logged yet.</p>
          ) : (
            <ul className="space-y-1.5 max-h-96 overflow-y-auto pr-1">
              {calls.map((c) => {
                const st = c.connected ? "connected" : c.missed ? "missed" : c.voicemail ? "voicemail" : "ringing";
                const label = c.matched ? (c.customer_name || fmtPhone(c.phone)) : fmtPhone(c.phone);
                return (
                  <li key={c.call_id} data-testid={`dialpad-call-${c.call_id}`} className="flex items-center gap-2 rounded-md border border-zinc-800 bg-zinc-950/60 px-2.5 py-1.5">
                    <span className={`flex-shrink-0 ${st === "missed" ? "text-rose-400" : st === "connected" ? "text-emerald-400" : st === "voicemail" ? "text-sky-400" : "text-amber-400"}`}>
                      {st === "missed" ? <PhoneMissed className="h-3.5 w-3.5"/> : st === "voicemail" ? <Voicemail className="h-3.5 w-3.5"/> : <Phone className="h-3.5 w-3.5"/>}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-semibold text-zinc-200 truncate flex items-center gap-1.5">
                        {label}
                        {c.matched ? <span className="text-[9px] uppercase tracking-wider text-emerald-300/80">known</span> : <span className="text-[9px] uppercase tracking-wider text-amber-300/80">new</span>}
                        {st === "missed" && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-600 text-white" data-testid={`dialpad-call-${c.call_id}-missed`}>Missed</span>}
                      </div>
                      <div className="text-[10px] text-zinc-500 truncate">
                        {st.charAt(0).toUpperCase() + st.slice(1)}
                        {c.assigned_user_name ? ` · → ${c.assigned_user_name}` : ""}
                        {c.trip_count ? ` · ${c.trip_count} trip${c.trip_count === 1 ? "" : "s"}` : ""}
                        {" · "}{fmtAgo(c.first_seen_at)}
                        {c.handled_at ? " · booked" : ""}
                      </div>
                    </div>
                    <Button size="sm" variant="outline" data-testid={`dialpad-call-${c.call_id}-start`} onClick={() => startBookingFromCall(c)} disabled={startBusy === c.call_id} className="h-7 px-2 text-[11px] bg-zinc-950 border-yellow-500/50 text-yellow-300 hover:bg-yellow-500/10 flex-shrink-0">{startBusy === c.call_id ? <Loader2 className="h-3 w-3 animate-spin"/> : <><PhoneCall className="h-3 w-3 mr-1"/>Start booking</>}</Button>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Per-user Dialpad number assignment table
// ════════════════════════════════════════════════════════════════════════
function UserAssignmentTable() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [drafts, setDrafts] = useState({});       // id -> in-progress number
  const [uidDrafts, setUidDrafts] = useState({}); // id -> in-progress screen-pop user id
  const [busy, setBusy] = useState(null);
  const [assignedCount, setAssignedCount] = useState(0);
  const [verifyBusy, setVerifyBusy] = useState(null);
  const [nowTs, setNowTs] = useState(Date.now());

  const load = useCallback(async ({ preserveDrafts = false } = {}) => {
    if (!preserveDrafts) setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/dialpad/users");
      const list = data?.users || [];
      setUsers(list);
      setAssignedCount(data?.assigned_count || 0);
      if (!preserveDrafts) {
        const d = {}, ud = {};
        list.forEach((u) => { d[u.id] = u.dialpad_number || ""; ud[u.id] = u.dialpad_user_id || ""; });
        setDrafts(d); setUidDrafts(ud);
      }
    } catch (e) {
      if (!preserveDrafts) toast.error(e?.response?.data?.detail || "Failed to load users");
    } finally { if (!preserveDrafts) setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  // Poll live-line status every 10s WITHOUT wiping any in-progress edits.
  useEffect(() => {
    const t = setInterval(() => { if (!document.hidden) load({ preserveDrafts: true }); }, 10000);
    return () => clearInterval(t);
  }, [load]);

  const saveAssignment = async (u) => {
    const val = (drafts[u.id] ?? "").trim();
    const uid = (uidDrafts[u.id] ?? "").trim();
    if (val === (u.dialpad_number || "") && uid === (u.dialpad_user_id || "")) { toast.message("No change"); return; }
    setBusy(u.id);
    try {
      await adminApi.put(`/admin/dialpad/users/${encodeURIComponent(u.id)}`, { dialpad_number: val, dialpad_user_id: uid });
      toast.success(val ? `Saved ${u.name} · ${fmtPhone(val)}` : `Cleared ${u.name}'s Dialpad number`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save assignment");
    } finally { setBusy(null); }
  };
  const clearAssignment = async (u) => {
    setBusy(u.id);
    try {
      await adminApi.put(`/admin/dialpad/users/${encodeURIComponent(u.id)}`, { dialpad_number: "", dialpad_user_id: "" });
      toast.success(`Cleared ${u.name}'s Dialpad number`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not clear");
    } finally { setBusy(null); }
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return users;
    return users.filter((u) => `${u.name} ${u.email} ${u.role} ${u.dialpad_number}`.toLowerCase().includes(s));
  }, [users, q]);
  const liveCount = useMemo(() => users.filter((u) => u.live).length, [users]);

  // Tick every second (only while someone is live) to drive the call-duration timers.
  useEffect(() => {
    if (liveCount === 0) return undefined;
    const t = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [liveCount]);

  const verifyScreenPop = async (u) => {
    const uid = (uidDrafts[u.id] ?? "").trim();
    if (!uid) { toast.error("Enter a Dialpad user id to verify"); return; }
    setVerifyBusy(u.id);
    try {
      const { data } = await adminApi.post("/admin/dialpad/verify-screenpop-id", { dialpad_user_id: uid });
      if (data.valid) toast.success(`Valid Dialpad user${data.name ? ` · ${data.name}` : ""}`);
      else toast.error(data.detail || "Invalid Dialpad user id");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed");
    } finally { setVerifyBusy(null); }
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="dialpad-users-card">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div>
          <h2 className="font-bold text-lg flex items-center gap-2"><Users className="h-4 w-4"/>User Dialpad Numbers</h2>
          <p className="text-[11px] text-zinc-500 mt-0.5" data-testid="dialpad-users-summary">
            {assignedCount} of {users.length} accounts assigned · calls route only to the assigned user&apos;s session.
            {liveCount > 0 && (
              <span className="ml-1 text-emerald-400 font-semibold" data-testid="dialpad-live-count">· {liveCount} on a call now</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-3.5 w-3.5 text-zinc-500 absolute left-2 top-1/2 -translate-y-1/2"/>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users…" data-testid="dialpad-users-search" className="bg-zinc-950 border-zinc-700 text-xs pl-7 h-8 w-44"/>
          </div>
          <Button variant="ghost" size="sm" data-testid="dialpad-users-refresh" onClick={load} className="h-8 px-2 text-zinc-400 hover:text-zinc-200">{loading ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <RefreshCw className="h-3.5 w-3.5"/>}</Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-zinc-400 text-sm py-6 justify-center"><Loader2 className="h-4 w-4 animate-spin"/>Loading accounts…</div>
      ) : filtered.length === 0 ? (
        <p className="text-[12px] text-zinc-600 italic py-4 text-center">No matching accounts.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
                <th className="text-left font-bold py-2 pr-3">User</th>
                <th className="text-left font-bold py-2 pr-3 hidden sm:table-cell">Role</th>
                <th className="text-left font-bold py-2 pr-3">Assigned Dialpad Number</th>
                <th className="text-right font-bold py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id} data-testid={`dialpad-user-row-${u.id}`} className="border-b border-zinc-800/60 hover:bg-zinc-800/20">
                  <td className="py-2 pr-3">
                    <div className="font-semibold text-zinc-200 truncate max-w-[190px] flex items-center gap-1.5">
                      {u.live && (
                        <span
                          data-testid={`dialpad-user-live-${u.id}`}
                          title={u.live_state === "connected" ? "On a call" : "Line ringing"}
                          className={`inline-block h-2 w-2 rounded-full flex-shrink-0 animate-pulse ${u.live_state === "connected" ? "bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.9)]" : "bg-amber-400 shadow-[0_0_6px_rgba(251,191,36,0.9)]"}`}
                        />
                      )}
                      <span className="truncate">{u.name}</span>
                      {!u.active && <span className="ml-1 text-[9px] text-zinc-500">(inactive)</span>}
                    </div>
                    <div className="text-[10px] text-zinc-500 truncate max-w-[190px]">{u.email}</div>
                  </td>
                  <td className="py-2 pr-3 hidden sm:table-cell">
                    <span className="text-[11px] text-zinc-400">{ROLE_LABEL[u.role] || u.role}</span>
                  </td>
                  <td className="py-2 pr-3">
                    <div className="flex items-center gap-1.5">
                      <Input
                        value={drafts[u.id] ?? ""}
                        onChange={(e) => setDrafts((d) => ({ ...d, [u.id]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === "Enter") saveAssignment(u); }}
                        placeholder="(unassigned)"
                        data-testid={`dialpad-user-input-${u.id}`}
                        className="bg-zinc-950 border-zinc-700 text-xs h-8 w-40 font-mono"
                      />
                      <Button size="sm" data-testid={`dialpad-user-save-${u.id}`} onClick={() => saveAssignment(u)} disabled={busy === u.id} className="h-8 px-2 text-[11px] bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">{busy === u.id ? <Loader2 className="h-3 w-3 animate-spin"/> : "Save"}</Button>
                      {u.assigned && (
                        <Button size="sm" variant="ghost" data-testid={`dialpad-user-clear-${u.id}`} onClick={() => clearAssignment(u)} disabled={busy === u.id} className="h-8 px-2 text-[11px] text-zinc-500 hover:text-rose-300">Clear</Button>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 mt-1">
                      <Input
                        value={uidDrafts[u.id] ?? ""}
                        onChange={(e) => setUidDrafts((d) => ({ ...d, [u.id]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === "Enter") saveAssignment(u); }}
                        placeholder="Screen-pop user id (optional)"
                        data-testid={`dialpad-user-uid-${u.id}`}
                        className="bg-zinc-950 border-zinc-700 text-[11px] h-7 w-52 font-mono"
                      />
                      <Button
                        size="sm" variant="outline"
                        data-testid={`dialpad-user-verify-${u.id}`}
                        onClick={() => verifyScreenPop(u)}
                        disabled={verifyBusy === u.id}
                        title="Ping Dialpad to confirm this screen-pop user id is valid"
                        className="h-7 px-2 text-[10px] bg-zinc-950 border-sky-500/50 text-sky-300 hover:bg-sky-500/10"
                      >
                        {verifyBusy === u.id ? <Loader2 className="h-3 w-3 animate-spin"/> : <><ShieldCheck className="h-3 w-3 mr-1"/>Verify</>}
                      </Button>
                    </div>
                  </td>
                  <td className="py-2 text-right align-top">
                    {u.live ? (
                      <div className="flex flex-col items-end gap-0.5">
                        <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-400/40 text-[10px]" data-testid={`dialpad-user-oncall-${u.id}`}><Phone className="h-3 w-3 mr-1"/>{u.live_state === "connected" ? "On a call" : "Ringing"}</Badge>
                        {u.live_since && (
                          <span className="text-[10px] text-emerald-400 font-mono tabular-nums" data-testid={`dialpad-user-timer-${u.id}`}>{fmtDuration(u.live_since, nowTs)}</span>
                        )}
                      </div>
                    ) : u.assigned ? (
                      <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-400/40 text-[10px]"><CheckCircle2 className="h-3 w-3 mr-1"/>Assigned</Badge>
                    ) : (
                      <Badge className="bg-amber-500/15 text-amber-300 border-amber-400/40 text-[10px]" data-testid={`dialpad-user-unassigned-${u.id}`}><AlertTriangle className="h-3 w-3 mr-1"/>Unassigned</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
